import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AgentResponse,
  KnowledgeBaseQueryParams,
  MetadataAttributes,
  RetrieveFilter
} from '../models/knowledge-base.models';

@Injectable({
  providedIn: 'root'
})
export class KnowledgeBaseService {
  private readonly apiBaseUrl = `${environment.apiBaseUrl}/bedrock/knowledge-base`;

  constructor(private http: HttpClient) {}

  /**
   * Generate a unique session ID for the conversation
   * Session ID must be 33+ characters for AWS Bedrock
   */
  generateSessionId(): string {
    // AWS Bedrock requires runtimeSessionId to be at least 33 characters
    const timestamp = Date.now().toString(36);
    const random1 = Math.random().toString(36).substring(2, 15);
    const random2 = Math.random().toString(36).substring(2, 15);
    const random3 = Math.random().toString(36).substring(2, 10);
    // Ensure minimum length of 33 characters: timestamp (~9) + 3 dashes + random1 (13) + random2 (13) + random3 (8) = ~56 chars
    return `kb-${timestamp}-${random1}-${random2}-${random3}`;
  }

  /**
   * Build metadata attributes from role and topic selections
   * Note: Role parameters are optional - backend will auto-determine roles from user account
   * @deprecated Role parameters are deprecated. Backend now automatically determines roles.
   */
  buildMetadataAttributes(
    tenantId: string,
    rolePublic?: boolean,
    roleAdmin?: boolean,
    roleSpeaker?: boolean,
    roleAttendee?: boolean,
    roleDm?: boolean,
    roleBm?: boolean,
    roleRep?: boolean,
    topic?: string,
    speakerTopics?: string[],
    attendeeTopics?: string[]
  ): MetadataAttributes {
    const metadata: MetadataAttributes = {
      tenantId: tenantId.trim()
    };

    // Role flags are now optional - backend will determine automatically
    // Only include if explicitly provided (for backward compatibility)
    if (rolePublic === true) metadata.role_public = true;
    if (roleAdmin === true) metadata.role_admin = true;
    if (roleSpeaker === true) metadata.role_speaker = true;
    if (roleAttendee === true) metadata.role_attendee = true;
    if (roleDm === true) metadata.role_dm = true;
    if (roleBm === true) metadata.role_bm = true;
    if (roleRep === true) metadata.role_rep = true;

    if (topic && topic.trim()) {
      metadata.topic = topic.trim();
    }

    if (speakerTopics && speakerTopics.length > 0) {
      metadata.speaker_topics = speakerTopics;
    }

    if (attendeeTopics && attendeeTopics.length > 0) {
      metadata.attendee_topics = attendeeTopics;
    }

    return metadata;
  }

  /**
   * Build retrieve filter for knowledge base query
   * Note: Role parameters are optional - backend will auto-determine roles from user account
   * @deprecated Role parameters are deprecated. Backend now automatically determines roles.
   */
  buildRetrieveFilter(
    tenantId: string,
    rolePublic?: boolean,
    roleAdmin?: boolean,
    roleSpeaker?: boolean,
    roleAttendee?: boolean,
    roleDm?: boolean,
    roleBm?: boolean,
    roleRep?: boolean,
    topic?: string
  ): RetrieveFilter {
    const filter: RetrieveFilter = {
      andAll: [
        {
          equals: {
            key: 'tenantId',
            value: tenantId.trim()
          }
        }
      ]
    };

    // Role filters are now optional - backend will determine automatically
    // Only include if explicitly provided (for backward compatibility)
    const roleFilters: Array<{ equals: { key: string; value: boolean } }> = [];
    if (rolePublic === true) roleFilters.push({ equals: { key: 'role_public', value: true } });
    if (roleAdmin === true) roleFilters.push({ equals: { key: 'role_admin', value: true } });
    if (roleSpeaker === true) roleFilters.push({ equals: { key: 'role_speaker', value: true } });
    if (roleAttendee === true) roleFilters.push({ equals: { key: 'role_attendee', value: true } });
    if (roleDm === true) roleFilters.push({ equals: { key: 'role_dm', value: true } });
    if (roleBm === true) roleFilters.push({ equals: { key: 'role_bm', value: true } });
    if (roleRep === true) roleFilters.push({ equals: { key: 'role_rep', value: true } });

    if (roleFilters.length > 0) {
      filter.andAll.push({ orAll: roleFilters });
    }

    if (topic && topic.trim()) {
      filter.andAll.push({
        equals: {
          key: 'topic',
          value: topic.trim()
        }
      });
    }

    return filter;
  }

  /**
   * Query the Bedrock Agent Core Knowledge Base
   * This method calls a backend API endpoint that handles AWS SDK interactions
   */
  async queryKnowledgeBase(params: KnowledgeBaseQueryParams): Promise<AgentResponse> {
    try {
      // Build metadata attributes
      // Note: Role parameters are optional - backend will automatically determine roles from user account
      const metadataAttributes = this.buildMetadataAttributes(
        params.tenantId,
        params.rolePublic,
        params.roleAdmin,
        params.roleSpeaker,
        params.roleAttendee,
        params.roleDm,
        params.roleBm,
        params.roleRep,
        params.topic,
        params.speakerTopics,
        params.attendeeTopics
      );

      // Build the payload — model and Knowledge Base ID are fixed on the backend,
      // not passed from the client.
      const payloadData: any = {
        user_question: params.userQuestion,
        metadataAttributes: metadataAttributes,
        runtimeSessionId: params.sessionId,
        numberOfResults: params.numberOfResults || 10,
        score: params.score || 0.3
      };

      if (params.conversationContext && params.conversationContext.length > 0) {
        payloadData.conversation_context = params.conversationContext;
      }

      const requestBody = { prompt: payloadData };

      const apiResponse = await firstValueFrom(
        this.http.post<{
          answer?: string;
          sources?: any[];
          metadata?: Record<string, any>;
          status?: string;
          error?: string;
        }>(
          `${this.apiBaseUrl}/invoke`,
          requestBody
        )
      );

      if (apiResponse.error) {
        throw new Error(apiResponse.error);
      }

      const response: AgentResponse = {
        answer: apiResponse.answer || 'No answer provided',
        sources: apiResponse.sources || [],
        metadata: apiResponse.metadata,
        status: apiResponse.status || 'success'
      };

      return response;
    } catch (error) {
      console.error('Error querying knowledge base:', error);

      let message = 'Failed to query knowledge base';
      if (error instanceof HttpErrorResponse) {
        message =
          error.error?.error?.message ||
          error.error?.message ||
          error.message ||
          message;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      throw new Error(message);
    }
  }

  /**
   * Test connectivity to the knowledge-base endpoint.
   * The backend controls all AWS routing (agent runtime, region, KB, model).
   */
  async testConnection(tenantId: string): Promise<boolean> {
    try {
      const testParams: KnowledgeBaseQueryParams = {
        sessionId: this.generateSessionId(),
        userQuestion: 'Hello',
        tenantId: tenantId,
        numberOfResults: 1,
        score: 0.3
      };

      await this.queryKnowledgeBase(testParams);
      return true;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
}

